import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {
  sendMessage, subscribeToMessages, acceptMessageRequest,
  declineMessageRequest, subscribeToConversations, subscribeToMessageRequests,
} from "../services/chatService";
import { getUserProfile } from "../services/userService";

export default function ChatScreen({ otherUser, onBack }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [conversations, setConversations] = useState([]);
  const [requests, setRequests] = useState([]);
  const [convProfiles, setConvProfiles] = useState({});
  const [activeConv, setActiveConv] = useState(otherUser || null);
  const [tab, setTab] = useState(otherUser ? "chat" : "inbox");
  const [isRequest, setIsRequest] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!user || user.isGuest) return;
    const unsubConvs = subscribeToConversations(user.uid, async (convs) => {
      setConversations(convs);
      const profiles = {};
      await Promise.all(convs.map(async (c) => {
        const otherUid = c.participants.find(p => p !== user.uid);
        if (otherUid && !profiles[otherUid]) {
          const p = await getUserProfile(otherUid);
          if (p) profiles[otherUid] = p;
        }
      }));
      setConvProfiles(profiles);
    });
    const unsubReqs = subscribeToMessageRequests(user.uid, async (reqs) => {
      setRequests(reqs);
    });
    return () => { unsubConvs(); unsubReqs(); };
  }, [user]);

  useEffect(() => {
    if (!activeConv || !user) return;
    const unsubMsgs = subscribeToMessages(user.uid, activeConv.uid, setMessages);
    const isReq = requests.some(r => r.participants?.includes(activeConv.uid));
    setIsRequest(isReq);
    return unsubMsgs;
  }, [activeConv, user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!text.trim() || !activeConv || !user) return;
    await sendMessage(user.uid, activeConv.uid, text.trim());
    setText("");
  };

  const handleAcceptRequest = async () => {
    await acceptMessageRequest(activeConv.uid, user.uid);
    setIsRequest(false);
  };

  const handleDeclineRequest = async () => {
    await declineMessageRequest(activeConv.uid, user.uid);
    setActiveConv(null);
    setTab("inbox");
  };

  if (!user || user.isGuest) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0A0A0A", padding: 40, textAlign: "center", fontFamily: "'DM Sans', sans-serif" }}>
        <div>
          <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#ffffff", marginBottom: 8, letterSpacing: -0.3 }}>Sign in to message</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.6 }}>Create an account to message your connections.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0A0A0A", display: "flex", flexDirection: "column", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 0; }
        .chat-header { padding: 16px 40px; border-bottom: 1px solid rgba(255,255,255,0.06); display: flex; align-items: center; gap: 12px; flex-shrink: 0; background: rgba(10,10,10,0.95); backdrop-filter: blur(20px); max-width: 900px; margin: 0 auto; width: 100%; }
        .chat-back { background: none; border: none; font-size: 20px; cursor: pointer; color: rgba(255,255,255,0.5); padding: 0; font-family: 'DM Sans', sans-serif; transition: color 0.2s; }
        .chat-back:hover { color: rgba(255,255,255,0.8); }
        .chat-avatar { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; background: rgba(255,255,255,0.08); display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; overflow: hidden; border: 1px solid rgba(255,255,255,0.1); }
        .chat-name { font-size: 15px; font-weight: 700; color: #ffffff; letter-spacing: -0.3px; }
        .chat-status { font-size: 11px; color: rgba(255,255,255,0.4); }
        .chat-tabs { display: flex; border-bottom: 1px solid rgba(255,255,255,0.06); flex-shrink: 0; max-width: 900px; margin: 0 auto; width: 100%; }
        .chat-tab { flex: 1; padding: 12px; border: none; background: none; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.35); cursor: pointer; border-bottom: 2px solid transparent; transition: all 0.2s; }
        .chat-tab.active { color: #ffffff; border-bottom-color: #1DB954; }
        .chat-messages { flex: 1; overflow-y: auto; padding: 24px 40px; display: flex; flex-direction: column; gap: 8px; max-width: 900px; margin: 0 auto; width: 100%; }
        .msg-bubble { max-width: 75%; padding: 10px 14px; font-size: 14px; line-height: 1.5; }
        .msg-mine { background: #1DB954; color: #ffffff; border-radius: 18px 18px 4px 18px; align-self: flex-end; }
        .msg-theirs { background: rgba(255,255,255,0.08); color: #ffffff; border-radius: 18px 18px 18px 4px; align-self: flex-start; }
        .chat-input-wrap { display: flex; align-items: center; gap: 10px; padding: 16px 40px 24px; border-top: 1px solid rgba(255,255,255,0.06); flex-shrink: 0; background: rgba(10,10,10,0.95); backdrop-filter: blur(20px); max-width: 900px; margin: 0 auto; width: 100%; }
        .chat-input { flex: 1; padding: 12px 16px; border-radius: 100px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); font-family: 'DM Sans', sans-serif; font-size: 14px; color: #ffffff; outline: none; transition: all 0.2s; }
        .chat-input:focus { border-color: rgba(255,255,255,0.3); }
        .chat-input::placeholder { color: rgba(255,255,255,0.3); }
        .chat-send-btn { width: 40px; height: 40px; border-radius: 50%; background: #1DB954; border: none; color: #ffffff; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; transition: all 0.2s; font-family: 'DM Sans', sans-serif; }
        .chat-send-btn:active { transform: scale(0.93); }
        .chat-send-btn:disabled { opacity: 0.3; cursor: not-allowed; }
        .conv-list { padding: 8px 40px 24px; display: flex; flex-direction: column; gap: 4px; flex: 1; overflow-y: auto; max-width: 900px; margin: 0 auto; width: 100%; }
        .conv-item { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 20px; cursor: pointer; transition: all 0.2s; border: 1px solid transparent; }
        .conv-item:active { background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.08); transform: scale(0.99); }
        .request-banner { margin: 12px 20px; background: rgba(29,185,84,0.08); border: 1px solid rgba(29,185,84,0.2); border-radius: 20px; padding: 14px 16px; }
        .request-banner-title { font-size: 13px; font-weight: 600; color: #1DB954; margin-bottom: 4px; }
        .request-banner-sub { font-size: 12px; color: rgba(255,255,255,0.5); margin-bottom: 12px; line-height: 1.5; }
        .request-actions { display: flex; gap: 8px; }
        .request-accept { flex: 1; padding: 10px; border-radius: 100px; background: #1DB954; border: none; color: #ffffff; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
        .request-accept:active { transform: scale(0.97); }
        .request-decline { flex: 1; padding: 10px; border-radius: 100px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); color: rgba(255,255,255,0.5); font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
        .request-decline:active { transform: scale(0.97); }
        .inbox-title { font-family: 'DM Serif Display', serif; font-size: clamp(32px, 4vw, 48px); color: #ffffff; letter-spacing: -2px; margin: 0 auto; padding: 80px 40px 0; max-width: 900px; text-align: center; }
      `}</style>

      {!activeConv ? (
        <>
          <div className="inbox-title">Messages</div>
          <div className="chat-tabs">
            <button className={`chat-tab ${tab === "inbox" ? "active" : ""}`} onClick={() => setTab("inbox")}>Inbox</button>
            <button className={`chat-tab ${tab === "requests" ? "active" : ""}`} onClick={() => setTab("requests")}>
              Requests {requests.length > 0 && `(${requests.length})`}
            </button>
          </div>

          <div className="conv-list">
            {tab === "inbox" && (
              conversations.filter(c => c.acceptedBy?.includes(user.uid)).length === 0 ? (
                <div style={{ padding: "40px 0", textAlign: "center" }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#ffffff", marginBottom: 6, letterSpacing: -0.3 }}>No messages yet</div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.5 }}>Connect with people at venues to start chatting</div>
                </div>
              ) : conversations.filter(c => c.acceptedBy?.includes(user.uid)).map(conv => {
                const otherUid = conv.participants.find(p => p !== user.uid);
                const p = convProfiles[otherUid];
                if (!p) return null;
                return (
                  <div key={conv.id} className="conv-item" onClick={() => setActiveConv(p)}>
                    <div className="chat-avatar">
                      {p.photoURL ? <img src={p.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span>👤</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#ffffff" }}>{p.displayName}</div>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{conv.lastMessage || ""}</div>
                    </div>
                  </div>
                );
              })
            )}

            {tab === "requests" && (
              requests.length === 0 ? (
                <div style={{ padding: "40px 0", textAlign: "center" }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📬</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#ffffff", marginBottom: 6, letterSpacing: -0.3 }}>No message requests</div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.5 }}>New message requests will appear here</div>
                </div>
              ) : requests.map(req => {
                const otherUid = req.participants?.find(p => p !== user.uid);
                const p = convProfiles[otherUid];
                return (
                  <div key={req.id} className="conv-item" onClick={() => { setActiveConv(p); setIsRequest(true); }}>
                    <div className="chat-avatar">
                      {p?.photoURL ? <img src={p.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span>👤</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#ffffff" }}>{p?.displayName || "User"}</div>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>Wants to message you</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      ) : (
        <>
          <div className="chat-header">
            <button className="chat-back" onClick={() => { setActiveConv(null); setTab("inbox"); }}>‹</button>
            <div className="chat-avatar">
              {activeConv.photoURL ? <img src={activeConv.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "👤"}
            </div>
            <div>
              <div className="chat-name">{activeConv.displayName || "User"}</div>
              <div className="chat-status">Connected</div>
            </div>
          </div>

          {isRequest && (
            <div className="request-banner">
              <div className="request-banner-title">Message Request</div>
              <div className="request-banner-sub">{activeConv.displayName} wants to message you. Accept to reply.</div>
              <div className="request-actions">
                <button className="request-accept" onClick={handleAcceptRequest}>Accept</button>
                <button className="request-decline" onClick={handleDeclineRequest}>Decline</button>
              </div>
            </div>
          )}

          <div className="chat-messages">
            {messages.length === 0 && (
              <div style={{ textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: 13, padding: "20px 0", lineHeight: 1.5 }}>
                Start the conversation
              </div>
            )}
            {messages.map((msg) => (
              <div key={msg.id} style={{ display: "flex", flexDirection: "column", alignItems: msg.from === user.uid ? "flex-end" : "flex-start" }}>
                <div className={`msg-bubble ${msg.from === user.uid ? "msg-mine" : "msg-theirs"}`}>
                  {msg.text}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {!isRequest && (
            <div className="chat-input-wrap">
              <input
                className="chat-input"
                placeholder="Message..."
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSend()}
              />
              <button className="chat-send-btn" onClick={handleSend} disabled={!text.trim()}>➤</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
